/**
 * KatoSelector — выбор активного КАТО (§3 FE-15, redesign по макету
 * «выборка КАТО»): зелёная плашка-триггер, под ней — поиск по населённым
 * пунктам и дерево «область → районы → СНП» (районы видны сразу, их дети —
 * за шевроном; бейдж — число прямых потомков).
 *
 * Открытие/закрытие можно контролировать снаружи (`open`/`onOpenChange`) —
 * ContextSidebar прячет остальной контент панели, пока идёт выбор.
 * Иерархия — по `parent_id` из GET /api/public/kato/list.
 */
import { Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronRight, MapPin, Search } from 'lucide-react';
import { matchPath, useLocation, useNavigate } from 'react-router-dom';

import { useKato } from '../../contexts/KatoContext';
import { getKatoList, type KatoListItem } from '../../requests/public.request';
import { cn } from '../../lib/cn';

interface KatoSelectorProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export const KatoSelector = ({
  open: openProp,
  onOpenChange,
}: KatoSelectorProps) => {
  const { t, i18n } = useTranslation();
  const { selectedKato, setSelectedKato } = useKato();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const [openState, setOpenState] = useState(false);
  const open = openProp ?? openState;
  const setOpen = (value: boolean) => {
    setOpenState(value);
    onOpenChange?.(value);
  };

  const [items, setItems] = useState<KatoListItem[]>([]);
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState<ReadonlySet<number>>(new Set());

  useEffect(() => {
    let cancelled = false;
    getKatoList()
      .then((res) => {
        if (!cancelled) setItems(res.items);
      })
      .catch((err) => {
        if (!cancelled) console.error('KatoSelector getKatoList failed', err);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const isRu = i18n.resolvedLanguage === 'ru';
  const nameOf = (item: KatoListItem) => (isRu ? item.name_ru : item.name_kz);

  const currentItem = items.find((item) => item.kato === selectedKato);
  const triggerLabel = currentItem
    ? nameOf(currentItem)
    : t('katoSelector.placeholder');

  // Подпись под выбранным: область (корень дерева) — когда выбран не корень.
  const rootItem = items.find((item) => item.parent_id === null);
  const triggerSubtitle =
    currentItem && currentItem.parent_id !== null && rootItem
      ? nameOf(rootItem)
      : null;

  const childrenOf = (kato: number) =>
    items.filter((item) => item.parent_id === kato);
  const roots = items.filter((item) => item.parent_id === null);

  const select = (kato: number) => {
    setSelectedKato(kato);
    setQuery('');
    setOpen(false);
    // Страница ЦП привязана к КАТО в URL (/:kato/digital-passport) —
    // без навигации выбор в селекторе не обновил бы её данные.
    if (matchPath('/:kato/digital-passport', pathname)) {
      navigate(`/${kato}/digital-passport`);
    }
  };

  const toggleExpand = (kato: number) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(kato)) {
        next.delete(kato);
      } else {
        next.add(kato);
      }
      return next;
    });

  const trimmed = query.trim().toLowerCase();
  const searchMatches = trimmed
    ? items.filter((item) =>
        `${item.name_ru} ${item.name_kz}`.toLowerCase().includes(trimmed),
      )
    : null;

  /** Строка дерева: имя (клик — выбор), шеврон-раскрытие, бейдж потомков. */
  const renderNode = (item: KatoListItem, depth: number) => {
    const children = childrenOf(item.kato);
    const isRoot = depth === 0;
    const isExpanded = isRoot || expanded.has(item.kato);
    return (
      <Fragment key={item.kato}>
        <div
          role="option"
          aria-selected={selectedKato === item.kato}
          className="flex items-center gap-1 rounded-lg p-2 transition-colors hover:bg-surface-muted"
          style={depth > 1 ? { paddingLeft: `${depth * 16}px` } : undefined}
        >
          {!isRoot && children.length > 0 ? (
            <button
              type="button"
              onClick={() => toggleExpand(item.kato)}
              aria-label={t('katoSelector.expand')}
              aria-expanded={isExpanded}
              className="shrink-0 rounded p-0.5 text-text-muted hover:text-text"
            >
              <ChevronRight
                className={cn(
                  'size-4 transition-transform',
                  isExpanded && 'rotate-90',
                )}
              />
            </button>
          ) : (
            !isRoot && <span className="w-5 shrink-0" />
          )}
          <button
            type="button"
            onClick={() => select(item.kato)}
            className={cn(
              'min-w-0 flex-1 truncate py-0.5 text-left text-[13.5px] text-text',
              isRoot ? 'font-bold' : 'font-medium',
            )}
          >
            {nameOf(item)}
          </button>
          {children.length > 0 && (
            <span className="shrink-0 rounded-full bg-surface-muted px-2 py-0.5 text-[11px] font-semibold tabular-nums text-text-muted">
              {children.length}
            </span>
          )}
        </div>
        {isExpanded && children.map((child) => renderNode(child, depth + 1))}
      </Fragment>
    );
  };

  return (
    <div className="flex flex-col gap-3">
      <button
        type="button"
        role="combobox"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        className="flex w-full items-center gap-3 rounded-2xl bg-gradient-to-r from-brand-500 to-brand-600 px-3.5 py-3 text-left text-white shadow-pop transition-opacity hover:opacity-95"
      >
        <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-white/20 ring-2 ring-white/70">
          <MapPin className="size-[18px]" />
        </span>
        <span className="min-w-0 flex-1">
          {/* Длинные названия районов переносятся на новую строку,
              а не обрезаются («район Магжана Жумабаева»). */}
          <span className="block break-words text-[15px] font-bold leading-snug">
            {triggerLabel}
          </span>
          {triggerSubtitle && (
            <span
              className="block text-[11.5px] leading-tight opacity-90"
              data-testid="kato-selector-subtitle"
            >
              {triggerSubtitle}
            </span>
          )}
        </span>
        <ChevronRight
          className={cn(
            'size-[18px] shrink-0 transition-transform',
            open && '-rotate-90',
          )}
        />
      </button>

      {open && (
        <>
          <div className="relative">
            <Search className="pointer-events-none absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-text-faint" />
            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={t('katoSelector.searchPlaceholder')}
              className="h-10 w-full rounded-full border border-surface-border bg-white pl-10 pr-4 text-[13.5px] text-text outline-none placeholder:text-text-faint focus:border-brand-500"
            />
          </div>

          <div
            role="listbox"
            className="flex flex-col rounded-2xl bg-white p-2 shadow-[0_1px_4px_rgba(0,0,0,0.06)]"
          >
            {searchMatches
              ? searchMatches.map((item) => (
                  <div
                    key={item.kato}
                    role="option"
                    aria-selected={selectedKato === item.kato}
                    className="rounded-lg transition-colors hover:bg-surface-muted"
                  >
                    <button
                      type="button"
                      onClick={() => select(item.kato)}
                      className="w-full truncate p-2 text-left text-[13.5px] font-medium text-text"
                    >
                      {nameOf(item)}
                    </button>
                  </div>
                ))
              : roots.map((root) => renderNode(root, 0))}
            {(searchMatches ? searchMatches.length : roots.length) === 0 && (
              <p className="px-2 py-3 text-center text-[13px] text-text-muted">
                {t('katoSelector.empty')}
              </p>
            )}
          </div>
        </>
      )}
    </div>
  );
};
